import ProfileImage from '@/components/ProfileImage'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Pencil } from 'lucide-react'
import { useState } from 'react'

type RowProps = {
  id: string
  rank: number
  totalRows: number
  user: any
  points: number
  feedback: string
  onPointsChange: (id: string, points: number) => void
  onFeedbackChange: (id: string, currentFeedback: string) => void
  onDelete?: (id: string) => void
}

export const BallotRow = ({
  id,
  rank,
  totalRows,
  user,
  points,
  feedback,
  onPointsChange,
  onFeedbackChange,
}: RowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const [localPoints, setLocalPoints] = useState<number | ''>(points ?? 0)

  const handlePointsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    if (value === '' || /^\d+$/.test(value)) {
      let numericValue: number = value === '' ? 0 : Number(value)
      if (value !== '') {
        if (numericValue < 1) numericValue = 1
        if (numericValue > 30) numericValue = 30
      }
      setLocalPoints(value === '' ? '' : numericValue)
      onPointsChange(id, value === '' ? 1 : numericValue)
    }
  }

  let borderColorClass = 'border-blue-500 text-black dark:text-white'
  if (rank === 1) borderColorClass = 'gradient-bg-affirmative text-white'
  else if (rank > totalRows - Math.ceil(totalRows * 0.1))
    borderColorClass = 'gradient-bg-red text-white'

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        flex items-center justify-between gap-3
        p-3 rounded-md border ${borderColorClass}
        bg-background min-h-14
      `}
    >
      {/* LEFT */}
      <div className="flex items-center gap-3 min-w-0">
        {/* drag handle ONLY: attributes+listeners go here */}
        <button
          type="button"
          className="cursor-grab flex items-center justify-center w-6 h-6 shrink-0"
          aria-label="Reorder"
          {...attributes}
          {...listeners}
        >
          <GripVertical size={18} />
        </button>

        <div className="h-6 w-8 shrink-0 flex items-center justify-center font-semibold leading-none">
          #{rank}
        </div>

        <ProfileImage user={user} className="w-10 h-10 shrink-0 rounded-full border object-cover" />

        <div className="flex-1 min-w-0 truncate font-bold">{user.displayName}</div>

        <Badge className="h-7 px-3 flex items-center whitespace-nowrap">
          {user.speakingRole} Team
        </Badge>
      </div>

      {/* RIGHT */}
      <div className="flex items-center gap-2 shrink-0">
        <Input
          value={localPoints}
          onChange={handlePointsChange}
          inputMode="numeric"
          className="h-9 w-16 text-center rounded bg-white dark:bg-accent font-bold border-2 border-gray-600 leading-none"
        />
        <Button
          className="h-9 w-9 p-0 border bg-gray-100 dark:bg-gray-900 text-black dark:text-white"
          variant="outline"
          size="icon"
          onClick={() => onFeedbackChange(id, feedback)}
        >
          <Pencil size={16} />
        </Button>
      </div>
    </div>
  )
}
